import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Tag } from 'antd';
import type { WorkflowNodeType, WorkflowPortKind } from '@gw/shared';

/** Mau theo loai cong: van ban / anh / video — dung chung cho handle + chu thich. */
export const PORT_COLOR: Record<WorkflowPortKind, string> = {
  text: '#2563eb',
  image: '#16a34a',
  video: '#9333ea',
};

export const PORT_LABEL: Record<WorkflowPortKind, string> = {
  text: 'Văn bản',
  image: 'Ảnh',
  video: 'Video',
};

export const NODE_TYPE_LABEL: Record<WorkflowNodeType, string> = {
  start: 'Bắt đầu',
  end: 'Kết thúc',
  model: 'Model',
  group: 'Nhóm model',
  text: 'Văn bản',
  image: 'Ảnh',
  video: 'Video',
  condition: 'Điều kiện',
};

/** Mau xoay vong cho cac nhanh cua node dieu kien. */
export const CASE_COLORS = ['#0ea5e9', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#14b8a6'];

/** Du lieu gan vao node cua ReactFlow (chi de hien thi — nguon that la WorkflowGraph). */
export interface RFNodeData extends Record<string, unknown> {
  label: string;
  nodeType: WorkflowNodeType;
  /** Cong vao cua node model/nhom model (suy tu paramsSchema). */
  inputs?: Array<{ name: string; label: string; kind: WorkflowPortKind }>;
  /** Loai dau ra (de to mau cong ra). */
  outputKind?: WorkflowPortKind;
  /** Mo ta phu (ten model/nhom, hoac template rut gon). */
  subtitle?: string;
  /** Node dang loi cau hinh (validate) -> vien do. */
  invalid?: boolean;
  /** Node dieu kien: cac nhanh de ve cong ra (id case + nhan hien thi). */
  cases?: Array<{ id: string; label: string }>;
}

const box = (accent: string, invalid?: boolean): React.CSSProperties => ({
  background: '#fff',
  border: `2px solid ${invalid ? '#dc2626' : accent}`,
  borderRadius: 12,
  padding: '10px 14px',
  minWidth: 190,
  boxShadow: '0 1px 4px rgba(15,23,42,0.08)',
  fontSize: 13,
});

export function StartNode({ data }: NodeProps) {
  const d = data as RFNodeData;
  return (
    <div style={{ ...box('#0ea5e9'), borderRadius: 999 }}>
      <b>▶ {d.label || 'Bắt đầu'}</b>
      <div style={{ fontSize: 11, color: '#64748b' }}>Nhận input của workflow</div>
      <Handle type="source" position={Position.Right} id="out" style={{ background: '#0ea5e9', width: 10, height: 10 }} />
    </div>
  );
}

export function EndNode({ data }: NodeProps) {
  const d = data as RFNodeData;
  return (
    <div style={{ ...box('#0f172a'), borderRadius: 999 }}>
      <Handle type="target" position={Position.Left} id="in" style={{ background: '#0f172a', width: 10, height: 10 }} />
      <b>■ {d.label || 'Kết thúc'}</b>
      <div style={{ fontSize: 11, color: '#64748b' }}>Trả kết quả cho phần mềm thứ 3</div>
    </div>
  );
}

/** Node chuc nang: Van ban / Anh / Video (hang so hoac template). */
export function FunctionNode({ data }: NodeProps) {
  const d = data as RFNodeData;
  const kind = (d.nodeType as WorkflowPortKind) ?? 'text';
  const color = PORT_COLOR[kind] ?? '#2563eb';
  return (
    <div style={box(color, d.invalid)}>
      <Handle type="target" position={Position.Left} id="in" style={{ background: color, width: 10, height: 10 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Tag color={color} style={{ margin: 0 }}>{PORT_LABEL[kind]}</Tag>
        <b>{d.label}</b>
      </div>
      {d.subtitle && (
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, maxWidth: 220, wordBreak: 'break-all' }}>
          {d.subtitle}
        </div>
      )}
      <Handle type="source" position={Position.Right} id="out" style={{ background: color, width: 10, height: 10 }} />
    </div>
  );
}

/**
 * Node MODEL / NHOM MODEL: cong vao theo paramsSchema (mau theo loai), 2 cong ra
 * thanh cong (xanh) / that bai (do).
 */
export function ModelNode({ data }: NodeProps) {
  const d = data as RFNodeData;
  const inputs = d.inputs ?? [];
  const outColor = PORT_COLOR[d.outputKind ?? 'image'];
  const rowH = 20;
  const headH = d.subtitle ? 52 : 34;
  const minHeight = headH + Math.max(inputs.length, 2) * rowH + 12;

  return (
    <div style={{ ...box(d.nodeType === 'group' ? '#f59e0b' : '#6366f1', d.invalid), minHeight, position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Tag color={d.nodeType === 'group' ? 'orange' : 'geekblue'} style={{ margin: 0 }}>
          {NODE_TYPE_LABEL[d.nodeType]}
        </Tag>
        <b>{d.label}</b>
      </div>
      {d.subtitle && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{d.subtitle}</div>}

      {/* Cong vao chung (moi duong noi toi node deu di vao day) */}
      <Handle type="target" position={Position.Left} id="in" style={{ background: '#94a3b8', width: 10, height: 10, top: 18 }} />

      {/* Chu thich cac tham so dau vao + mau theo loai */}
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {inputs.slice(0, 6).map((p) => (
          <div key={p.name} style={{ fontSize: 11, color: PORT_COLOR[p.kind], display: 'flex', gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: 8, background: PORT_COLOR[p.kind], display: 'inline-block', marginTop: 4 }} />
            {p.label}
          </div>
        ))}
        {inputs.length > 6 && <div style={{ fontSize: 11, color: '#94a3b8' }}>… +{inputs.length - 6} tham số</div>}
      </div>

      {/* 2 cong ra */}
      <div style={{ position: 'absolute', right: 8, top: headH, fontSize: 10, color: '#16a34a' }}>thành công</div>
      <Handle
        type="source"
        position={Position.Right}
        id="success"
        style={{ background: '#16a34a', width: 11, height: 11, top: headH + 6 }}
      />
      <div style={{ position: 'absolute', right: 8, top: headH + 22, fontSize: 10, color: '#dc2626' }}>thất bại</div>
      <Handle
        type="source"
        position={Position.Right}
        id="failure"
        style={{ background: '#dc2626', width: 11, height: 11, top: headH + 28 }}
      />
      <div style={{ position: 'absolute', right: -2, bottom: 4, width: 6, height: 6, borderRadius: 6, background: outColor }} />
    </div>
  );
}

/**
 * Node DIEU KIEN: 1 cong vao + moi truong hop 1 cong ra (mau xoay vong) + cong "còn lại".
 * Xet tu tren xuong, truong hop khop DAU TIEN thang.
 */
export function ConditionNode({ data }: NodeProps) {
  const d = data as RFNodeData;
  const cases = d.cases ?? [];
  const headH = d.subtitle ? 52 : 34;
  const rowH = 22;

  return (
    <div style={{ ...box('#0891b2', d.invalid), position: 'relative', paddingRight: 20, minWidth: 210 }}>
      <Handle type="target" position={Position.Left} id="in" style={{ background: '#0891b2', width: 10, height: 10, top: 18 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Tag color="cyan" style={{ margin: 0 }}>Điều kiện</Tag>
        <b>{d.label}</b>
      </div>
      {d.subtitle && (
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, maxWidth: 230, wordBreak: 'break-all' }}>{d.subtitle}</div>
      )}
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {cases.map((c, i) => (
          <div key={c.id} style={{ fontSize: 11, color: CASE_COLORS[i % CASE_COLORS.length], textAlign: 'right' }}>
            {c.label}
          </div>
        ))}
        <div style={{ fontSize: 11, color: '#64748b', textAlign: 'right' }}>còn lại</div>
      </div>
      {cases.map((c, i) => (
        <Handle
          key={c.id}
          type="source"
          position={Position.Right}
          id={`case:${c.id}`}
          style={{ background: CASE_COLORS[i % CASE_COLORS.length], width: 11, height: 11, top: headH + 10 + i * rowH }}
        />
      ))}
      <Handle
        type="source"
        position={Position.Right}
        id="else"
        style={{ background: '#64748b', width: 11, height: 11, top: headH + 10 + cases.length * rowH }}
      />
    </div>
  );
}

export const nodeTypes = {
  start: StartNode,
  end: EndNode,
  model: ModelNode,
  group: ModelNode,
  text: FunctionNode,
  image: FunctionNode,
  video: FunctionNode,
  condition: ConditionNode,
};
